import _, { round } from 'lodash';
import { toast } from 'react-toastify';
import i18n from 'i18n';

import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import { AgencyManager, DefaultAgencyManager } from 'core/agency/AgencyManager';
import { multiply100, toDecimal } from 'utils/Math';
import { DynamicBreadcrumb } from 'components/Breadcrumbs/DynamicBreadcrumbs';
import { renderErrors } from 'containers/Creatives/CreativeSetupFlow/FlowSteps/SubSteps/FormContent/CreativeFormHintRenderFunction';
import { formatBytes } from 'utils/StringUtil';
import mimeDB from 'mime-db';

export type AgencyFormForAgencyState = {
  isLoading: boolean;
  redirectUrl?: string;
};

export interface AgencyFormForAgencyModel {
  readonly agencyId: number;
  readonly cancelPath: string;
  readonly title: string;
  readonly defaultAgcData: any;
  readonly breadcrumbs: any;
  state: AgencyFormForAgencyState;
  event: UpdateEventListener<AgencyFormForAgencyModel>;
  save(value: any): void;
  init(): Promise<void>;
  onUnmount(handler): void;
  validate(basic): any;
}

export interface AgencyFormForAgencyProps {
  readonly model: AgencyFormForAgencyModel;
}

export class DefaultAgencyFormForAgencyModel implements AgencyFormForAgencyModel {
  modelEvent: FireableUpdateEventListener<AgencyFormForAgencyModel>;
  agencyManager: AgencyManager;
  agencyId: number;
  isLoading: boolean;
  redirectUrl?: string;
  agency?: any;

  constructor(
    agencyId: number,
    private updateLocalMeta: () => Promise<void>,
    agencyManager: AgencyManager = new DefaultAgencyManager()
  ) {
    this.agencyId = agencyId;
    this.agencyManager = agencyManager;
    this.modelEvent =
      new FireableUpdateEventListener<AgencyFormForAgencyModel>();
    this.isLoading = true;
  }

  async init(): Promise<void> {
    try {
      const { detail } = await this.agencyManager.fetchAgency(this.agencyId);
      this.agency = detail;
      this.updateState(false);
    } catch (e) {
      this.updateState(false, '/');
    }
  }

  get title() {
    return 'agency.form.titles.edit';
  }

  get breadcrumbs() {
    return [
      {
        path: '/agencies/:agencyId',
        breadcrumb: DynamicBreadcrumb,
        props: {
          label: _.get(this.agency, 'companyName'),
          matchParam: 'agencyId'
        }
      },
      {
        path: '/agencies/:agencyId/edit',
        breadcrumb: DynamicBreadcrumb,
        props: {
          prefix: i18n.t('common.labels.edit'),
          label: _.get(this.agency, 'companyName'),
          matchParam: 'agencyId'
        }
      }
    ];
  }

  get cancelPath() {
    return `/agencies/${this.agencyId}`;
  }

  get event(): UpdateEventListener<AgencyFormForAgencyModel> {
    return this.modelEvent;
  }

  get defaultAgcData() {
    if (!this.agency) {
      return {};
    }
    const agcPercent = _.get(this.agency, 'agcPercent', 0);
    const modelAgcPercent = round(multiply100(agcPercent), 2);
    const maxOrderProfit = _.get(this.agency, 'addonProps.maxOrderProfit', 0);
    const modelMaxOrderProfit = round(multiply100(maxOrderProfit), 2);
    return {
      ...this.agency,
      agcPercent: modelAgcPercent,
      addonProps: {
        ..._.get(this.agency, 'addonProps', {}),
        maxOrderProfit: modelMaxOrderProfit
      }
    };
  }

  async save(agencyFormikObj: any): Promise<void> {
    this.updateState(true);
    try {
      const {
        companyId,
        agcPercent,
        logoData,
        adLogo,
        adDeclaration,
        addonProps
      } = agencyFormikObj;
      await this.agencyManager.updateAgencySelf(this.agencyId, {
        companyId,
        addonProps,
        agcPercent: toDecimal(agcPercent),
        logoData,
        adLogo,
        adDeclaration
      });
      await this.updateLocalMeta();
      toast.success(i18n.t('common.messages.succeeded'));
      this.updateState(false, `/agencies/${this.agencyId}`);
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState(false);
    }
  }

  validateAdDeclaration(
    fileData: FileFieldData,
    isExpressPass: boolean
  ): string | JSX.Element | undefined {
    if (!fileData || (!fileData.file && !fileData.url)) {
      return isExpressPass
        ? undefined
        : i18n.t('formValidate.labels.emptyError');
    }

    if (!fileData.file) {
      return;
    }

    let errors: string[] = [];
    const file = fileData.file;
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (validTypes.indexOf(file.type) === -1) {
      const extensions = _.get(mimeDB[file.type], 'extensions', ['Unknown']);
      return i18n.t('formikConfigInputField.errors.fileType', {
        type: extensions[0],
        types: 'pdf, doc, docx'
      });
    }
    if (file.size > 5242880) {
      errors.push(
        i18n.t('creativeSetupFlow.labels.storageErrorHint', {
          storage1: formatBytes(file.size),
          storage2: '5MB'
        })
      );
    }
    return errors.length > 0 ? renderErrors(errors) : undefined;
  }

  validate = (agency) => {
    const maxOrderProfit = _.get(agency, 'addonProps.maxOrderProfit', 0);
    let error = {};
    if (agency.agcPercent > maxOrderProfit) {
      error = {
        ...error,
        agcPercent: i18n.t(
          'agencyForm.labels.orderDefaultProfitBiggerThanMaxProfit'
        )
      };
    }
    const hasAnyChannelExpressPassEnabled = _.get(
      agency,
      'addonProps.expressPass',
      false
    );
    const adDeclarationError = this.validateAdDeclaration(
      agency.adDeclaration,
      hasAnyChannelExpressPassEnabled
    );
    if (adDeclarationError) {
      error = {
        ...error,
        adDeclaration: adDeclarationError
      };
    }
    return error;
  };

  updateState(isLoading: boolean, redirectUrl?: string) {
    this.isLoading = isLoading;
    this.redirectUrl = redirectUrl;
    this.modelEvent.fireEvent(this);
  }

  get state(): AgencyFormForAgencyState {
    return {
      isLoading: this.isLoading,
      redirectUrl: this.redirectUrl
    };
  }

  onUnmount(handler): void {
    handler && this.event.remove(handler);
    this.redirectUrl = undefined;
  }
}
